/**
 * <cart-toast>
 *
 * Event-based add-to-cart confirmation toast (top-right). Replaces Dawn's
 * cart-notification popup as the *presentation* for cart additions.
 *
 * Purely reactive: it listens for the `ThemeEvents.cartToastShow` event
 * (dispatched by cart-notification.showToast() in place of opening the popup)
 * and never fetches or renders cart sections itself — the cart bubble is still
 * updated by the retained cart-notification plumbing.
 *
 * `detail.item` (image, title, variant) adds the product row; `detail.message`
 * overrides the status line. Each toast stacks, animates in and self-dismisses;
 * clicking it dismisses early.
 */
import { ThemeEvents } from '@theme/events';

const AUTO_DISMISS_MS = 2500;
const EXIT_MS = 300;
const THUMBNAIL_WIDTH = 120;
const CHECK_ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>';

class CartToast extends HTMLElement {
  connectedCallback() {
    this.messageAdded = this.dataset.messageAdded || 'Added to bag';
    this.viewLabel = this.dataset.viewLabel || 'View bag';
    this.cartUrl = this.dataset.cartUrl || '/cart';
    this.onToastShow = this.handleToastShow.bind(this);
    document.addEventListener(ThemeEvents.cartToastShow, this.onToastShow);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartToastShow, this.onToastShow);
  }

  handleToastShow(event) {
    const detail = event?.detail || {};
    this.show(detail.message || this.messageAdded, detail.item);
  }

  /**
   * Render a self-dismissing toast.
   * @param {string} message
   * @param {{ image?: string, title?: string, variant?: string }} [item]
   */
  show(message, item) {
    const toast = document.createElement('div');
    toast.className = 'cart-toast__item';
    toast.style.setProperty('--toast-duration', `${AUTO_DISMISS_MS}ms`);

    // Status strip spans the full width; the product row sits below it.
    const status = document.createElement('div');
    status.className = 'cart-toast__status';
    status.innerHTML = CHECK_ICON;
    status.append(message);
    toast.appendChild(status);

    const row = document.createElement('div');
    row.className = 'cart-toast__row';

    const thumbnailUrl = this.thumbnailUrl(item?.image);
    if (thumbnailUrl) {
      const image = document.createElement('img');
      image.className = 'cart-toast__media';
      image.src = thumbnailUrl;
      image.alt = '';
      image.width = 40;
      image.height = 53;
      row.appendChild(image);
    }

    const body = document.createElement('div');
    body.className = 'cart-toast__body';
    if (item?.title) body.appendChild(this.textLine('cart-toast__title', item.title));
    if (item?.variant) body.appendChild(this.textLine('cart-toast__variant', item.variant));
    row.appendChild(body);

    const link = document.createElement('a');
    link.className = 'cart-toast__link';
    link.href = this.cartUrl;
    link.textContent = this.viewLabel;
    row.appendChild(link);
    toast.appendChild(row);

    this.appendChild(toast);

    // Force a reflow so the enter transition runs from the off-screen state.
    void toast.offsetWidth;
    toast.classList.add('cart-toast__item--visible');

    const timer = setTimeout(() => this.dismiss(toast), AUTO_DISMISS_MS);
    toast.addEventListener('click', (event) => {
      if (event.target.closest('a')) return;
      clearTimeout(timer);
      this.dismiss(toast);
    });
  }

  textLine(className, text) {
    const line = document.createElement('span');
    line.className = className;
    line.textContent = text;
    return line;
  }

  // Prefer the exact URL the card / gallery already loaded for this image: the cart
  // API returns a cdn.shopify.com URL, while the theme renders /cdn/shop/… with its
  // own width, so only reusing that one is a cache hit. Else a small fixed width.
  thumbnailUrl(imageUrl) {
    if (!imageUrl) return null;
    try {
      const url = new URL(imageUrl, window.location.origin);
      const loadedUrl = this.findLoadedImage(url);
      if (loadedUrl) return loadedUrl;
      url.searchParams.set('width', String(THUMBNAIL_WIDTH));
      return url.toString();
    } catch {
      return null;
    }
  }

  /**
   * Smallest already-loaded <img> on the page showing the same file and version.
   * @param {URL} imageUrl
   * @returns {string | null}
   */
  findLoadedImage(imageUrl) {
    const fileName = imageUrl.pathname.split('/').pop();
    const version = imageUrl.searchParams.get('v');
    if (!fileName) return null;

    let bestImage = null;
    for (const image of document.images) {
      if (!image.complete || !image.naturalWidth || !image.currentSrc) continue;
      if (image.currentSrc.startsWith('data:')) continue;
      let candidateUrl;
      try {
        candidateUrl = new URL(image.currentSrc);
      } catch {
        continue;
      }
      if (candidateUrl.pathname.split('/').pop() !== fileName) continue;
      if (version && candidateUrl.searchParams.get('v') !== version) continue;
      if (!bestImage || image.naturalWidth < bestImage.naturalWidth) bestImage = image;
    }
    return bestImage ? bestImage.currentSrc : null;
  }

  /** @param {HTMLElement} toast */
  dismiss(toast) {
    if (!toast.isConnected) return;
    toast.classList.remove('cart-toast__item--visible');
    toast.classList.add('cart-toast__item--leaving');
    toast.addEventListener('transitionend', () => toast.remove(), { once: true });
    // Fallback in case transitionend never fires (e.g. reduced-motion / tab hidden).
    setTimeout(() => toast.remove(), EXIT_MS + 100);
  }
}

if (!customElements.get('cart-toast')) {
  customElements.define('cart-toast', CartToast);
}
